import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { sha512 } from 'js-sha512';
import classNames from 'classnames';

import iveri from '../../../../assets/images/iveri.png';
import ozow from '../../../../assets/images/ozow.png';
import Button from 'src/components/Button/Button';
import Loading from '../../../Loading/Loading';
import useIsMobile from 'src/hooks/useIsMobile';
import { Customer } from 'src/interfaces/AuthState';

interface Props {
  limits: {
    depositLimitDay: number
  },
  customer: Customer,
  wallet: any,
  selectedPaymentMethod: string
}

function PaymentMethods({ limits, selectedPaymentMethod, customer, wallet:{ balance, currency } } : Props) {
  const [amount, setAmount] = useState('0.00');
  const [state, setState] = useState({ loading: false, showIframe: false });
  const [messageError, setMessageError] = useState('');
  const [messageSuccess, setMessageSuccess] = useState('');
  const [idCardNumber, setIdCardNumber] = useState(customer.personalIdNumber);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const mobile = useIsMobile();

  const maxDeposit = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'ZAR' }).format(limits.depositLimitDay);
  const minDeposit = 10;

  useEffect(() => {
    if (selectedPaymentMethod === 'iveri'){
      window['iveriCallback'] = (data) => {
        const parsedData = data && JSON.parse(data);
        const message  = t('depositFunds.successTransaction');
        setMessageSuccess(message);
        setMessageError('');
        if (parsedData.Lite_Result_Description !== '') {
          setMessageSuccess('');
          setMessageError(parsedData.Lite_Result_Description);
        }

        setState({
          showIframe: false,
          ...state
        });
      };
    }

    return () => {
      delete window['iveriCallback'];
    };
  }, []);

  const handleAmountChange = (e) => {
    e.preventDefault();
    setAmount(e.target.value);
  };

  const createHashCheck = (postData) => {
    let concat = '';

    //concatenat all values
    for (const elem in postData) {
      concat += postData[elem].toString();
    }

    //Add private key
    concat += process.env.REACT_APP_IPAY_PRIVATE_KEY;

    //Transform all values to lower case
    const loweredConcat = concat.toLowerCase();

    //hash the lowered case concat into an sha512
    const hash = sha512(loweredConcat);

    return hash;
  };

  // eslint-disable-next-line no-unused-vars
  const submitToOzow = () => {
    if (selectedPaymentMethod === 'ozow') {
      const paymentUrl = 'https://pay.ozow.com/';
      const transactionReference = window.performance.now().toString();
      const postData = {
        'SiteCode': 'TSTSTE0001',
        'CountryCode': 'ZA',
        'CurrencyCode': 'ZAR',
        'Amount': +amount,
        'TransactionReference': transactionReference.substring(0, 10),
        'BankReference': 'ozow-' + transactionReference.substring(0, 10),
        'Optional1': customer.accountNumber,
        'NotifyUrl': `${ process.env.REACT_APP_XTURF }/public/ozow/notify`,
        'IsTest': true,
        'HashCheck': ''
      };

      postData.HashCheck = createHashCheck(postData);
      window['ozowPayment'].createPaymentFrame('ozowPaymentContainer', paymentUrl, postData);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    const valid = await dispatch.account.checkValidity(amount);

    if (valid) {
      const href = window.location.href;
      const newUrl = href[href.length -1] === '/' ? href.substring(0, href.length - 1) : href;
      const url =  await dispatch.account.submitOzow({
        amount,
        personalIdNumber: idCardNumber,
        cancelUrl: `${ newUrl }/#payment-cancel`,
        divisor: 1,
        errorUrl:  `${ newUrl }/#payment-error`,
        successUrl:  `${ newUrl }/#payment-success`,
        currency,
        customer: customer.id
      });
      if (url !== ''){
        location.replace(url);
      }
    }
  };

  return (
    <form
      className="deposit-funds__tab-panel"
      onSubmit={ onSubmit }
    >
      {
        state.showIframe && selectedPaymentMethod === 'iveri' && (
          <>
            <Loading contained forceLoading={ state.loading } />
            <iframe
              style={ { visibility: state.loading ? 'hidden' : 'visible', margin: '0 auto', display: 'block', minHeight: '65vh', overflow: 'auto' } }
              title="iveri"
              src={ `/iveri.html?amount=${ +amount * 100 }&email=${ customer.email }` }
              width={ mobile ? '100%' : '750px' }
            />
          </>
        )
      }

      {
        state.showIframe && selectedPaymentMethod === 'ozow' && (
          <>
            <Loading contained forceLoading={ state.loading } />
            <div
              id="ozowPaymentContainer"
              style={
                {
                  visibility: state.loading ? 'hidden' : 'visible',
                  margin: '0 auto',
                  display: 'block',
                  width: mobile ? '100%' : '750px',
                  height: mobile ? '65vh' : '500px' }
              }
            ></div>
          </>
        )
      }

      {
        !state.showIframe &&
   <>
     <div className={
       classNames({
         'deposit-funds__payment-method': true,
         'deposit-funds__payment-method--selected': selectedPaymentMethod === 'iveri'
       })
     }
     >
       <div className="deposit-funds__payment-method-wrapper">
         <img
           className="deposit-funds__payment-method__image"
           src={ selectedPaymentMethod === 'iveri' ? iveri : ozow }
           alt="iveri"
         />
         <span className="deposit-funds__details-title">
           { t('depositFunds.userDetails') }
         </span>
         <ul className="deposit-funds__payment-method--values">
           <li>{ t('depositFunds.name') }: { `${ customer.firstName } ${ customer.lastName }` }</li>
           <li>{ t('depositFunds.balance') }: { `${ balance / 100 } ${ currency }` } </li>
         </ul>
         <span className="deposit-funds__details-title">
           { t('depositFunds.requiredInformation') }
         </span>
         <div className="deposit-funds__double-container">
           <div className="deposit-funds__container">
             <label className="deposit-funds__label deposit-funds__label--double">{ t('depositFunds.amount') }</label>
             <label className="deposit-funds__label deposit-funds__label--double">{ t('depositFunds.currency') }</label>
             <label className="deposit-funds__label deposit-funds__label--double">{ t('depositFunds.idCardNumber') }</label>
           </div>
           <div className="deposit-funds__container">
             <input
               className="deposit-funds__input deposit-funds__input--double"
               value={ amount }
               required
               type="number"
               name="amount"
               onChange={ handleAmountChange }
             />
             <span className="deposit-funds__currency" id="currency">{ currency }</span>
             <input
               className="deposit-funds__input deposit-funds__input--double"
               value={ idCardNumber }
               required
               type="number"
               name="idCard"
               onChange={ (e) => setIdCardNumber(+(e.target.value)) }
             />
           </div>
           {
             mobile &&
              <>
                <div className="deposit-funds__container">
                  <label className="deposit-funds__label">{ t('depositFunds.amount') }</label>
                </div>
                <div className="deposit-funds__container">
                  <input
                    className="deposit-funds__input"
                    value={ idCardNumber }
                    required
                    type="number"
                    name="idCard"
                    onChange={ (e) => setIdCardNumber(+(e.target.value)) }
                  />
                </div>
                <ul className="deposit-funds__payment-method--deposit-values">
                  <li>{ t('depositFunds.minimum') }: { minDeposit }</li>
                  <li>{ t('depositFunds.maximum') }: { maxDeposit }</li>
                  <li>{ t('depositFunds.timeProcess') }: Instant</li>
                </ul>
              </>
           }
         </div>
       </div>
     </div>
     <div className="deposit-funds__btn">
       <Button
         type="submit"
         className="btn--cta"
         value={ selectedPaymentMethod === 'ozow' ? 'ozow' : '' }
         disabled={ +amount <= 0 || +amount > limits.depositLimitDay || +amount < minDeposit }
         text={ t('depositFunds.deposit') }
       />
     </div>
   </>
      }
      {
        messageError && (
          <span className="deposit-funds__payment-method__title deposit-funds__payment-method__title--error">{ messageError }</span>
        )
      }
      {
        messageSuccess && (
          <span className="deposit-funds__payment-method__title deposit-funds__payment-method__title--success">{ messageSuccess }</span>
        )
      }
      {
        state.showIframe && (
          <Button
            className="btn--outlined"
            text={ t('depositFunds.back')  }
            onClick={ () => setState({ ...state, showIframe: false }) }
            type = "button"
          />
        )
      }
    </form>
  );
}

export default PaymentMethods;
